import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { useAttachmentBlob } from '../hooks/useAttachmentBlob';
import styles from './ImageLightbox.module.css';

interface Attachment { attachmentId: string; localBlobUrl?: string; }
interface IImageLightboxProps {
  siteId: string;
  issueId: string;
  attachments: Attachment[];
  initialIndex: number;
  onClose: () => void;
}

export function ImageLightbox({ siteId, issueId, attachments, initialIndex, onClose }: IImageLightboxProps) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(initialIndex);

  const current = attachments[index];
  const total = attachments.length;
  const canPrev = index > 0;
  const canNext = index < total - 1;

  const remote = useAttachmentBlob(
    siteId,
    issueId,
    current && !current.localBlobUrl ? current.attachmentId : null,
  );
  const url = current?.localBlobUrl ?? remote.url;

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // The innermost overlay owns Escape: stop it here so the issue drawer
        // underneath (a document listener) doesn't close along with us.
        e.stopPropagation();
        onClose();
      } else if (e.key === 'ArrowLeft' && canPrev) setIndex((i) => i - 1);
      else if (e.key === 'ArrowRight' && canNext) setIndex((i) => i + 1);
    }
    // Capture phase on window: runs before any document/bubble listener.
    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [canPrev, canNext, onClose]);

  if (!current) return null;

  return createPortal(
    <div className={styles.backdrop} onClick={onClose} role="dialog" aria-modal="true" aria-label={t('issues.hub.lightboxLabel')}>
      <button
        type="button"
        className={styles.closeBtn}
        onClick={onClose}
        aria-label={t('issues.hub.lightboxClose')}
      >×</button>
      {canPrev && (
        <button
          type="button"
          className={`${styles.navBtn} ${styles.prevBtn}`}
          onClick={(e) => { e.stopPropagation(); setIndex((i) => i - 1); }}
          aria-label={t('issues.hub.lightboxPrev')}
        >‹</button>
      )}
      {canNext && (
        <button
          type="button"
          className={`${styles.navBtn} ${styles.nextBtn}`}
          onClick={(e) => { e.stopPropagation(); setIndex((i) => i + 1); }}
          aria-label={t('issues.hub.lightboxNext')}
        >›</button>
      )}
      <div className={styles.imageWrap} onClick={(e) => e.stopPropagation()}>
        {url ? (
          <img src={url} alt={t('issues.hub.commentImageAlt')} className={styles.image} />
        ) : (
          <div className={styles.loadingTxt}>{t('issues.hub.thumbnailLoading')}</div>
        )}
      </div>
    </div>,
    document.body,
  );
}
